/**
 * System capture — location and photographs.
 *
 * Spec §10 is specific that date/time and GPS are captured by the system and not
 * typed by the user. That is an evidence requirement rather than a convenience
 * one: a coordinate somebody typed proves nothing about where they stood.
 */

export type GpsCaptureStatus = 'captured' | 'denied' | 'unavailable' | 'timed_out';

export interface GpsFix {
  status: GpsCaptureStatus;
  latitude: number | null;
  longitude: number | null;
  accuracyMetres: number | null;
}

/** Rule 1: a timeout is a setting, not a number buried in a call. */
const GPS_TIMEOUT_MS = Number(import.meta.env.VITE_GPS_TIMEOUT_MS ?? 15_000);

const UNRESOLVED: Omit<GpsFix, 'status'> = {
  latitude: null,
  longitude: null,
  accuracyMetres: null,
};

/**
 * Ask the device where it is.
 *
 * This never rejects. A refused permission, a phone inside a steel frame and a
 * browser with no geolocation at all are all ordinary outcomes on a construction
 * site, and none of them is a reason to stop someone recording what they saw. The
 * REASON is returned instead, so the record distinguishes "nobody tried" from
 * "there was no signal in the basement" — which is what makes the missing
 * coordinate readable evidence rather than a gap.
 */
export function captureGps(): Promise<GpsFix> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({ status: 'unavailable', ...UNRESOLVED });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          status: 'captured',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMetres:
            typeof position.coords.accuracy === 'number' ? position.coords.accuracy : null,
        }),
      (error) => {
        const status: GpsCaptureStatus =
          error.code === error.PERMISSION_DENIED
            ? 'denied'
            : error.code === error.TIMEOUT
              ? 'timed_out'
              : 'unavailable';
        resolve({ status, ...UNRESOLVED });
      },
      { enableHighAccuracy: true, timeout: GPS_TIMEOUT_MS, maximumAge: 0 },
    );
  });
}

export function describeGps(fix: {
  gpsCaptureStatus: string;
  gpsLatitude: number | string | null;
  gpsLongitude: number | string | null;
}): string {
  switch (fix.gpsCaptureStatus) {
    case 'captured':
      return `${Number(fix.gpsLatitude).toFixed(5)}, ${Number(fix.gpsLongitude).toFixed(5)}`;
    case 'denied':
      return 'Location permission was refused';
    case 'timed_out':
      return 'The device could not get a fix in time';
    default:
      return 'Location was unavailable on this device';
  }
}

// ---------------------------------------------------------------------------
// Photographs
// ---------------------------------------------------------------------------

export interface ResizedImage {
  data: string;
  contentType: 'image/jpeg';
  width: number;
  height: number;
  byteLength: number;
}

const MAX_WIDTH = Number(import.meta.env.VITE_PHOTO_MAX_WIDTH ?? 1920);
const MAX_HEIGHT = Number(import.meta.env.VITE_PHOTO_MAX_HEIGHT ?? 1080);
const JPEG_QUALITY = Number(import.meta.env.VITE_PHOTO_JPEG_QUALITY ?? 0.8);

/**
 * Resize to at most 1920x1080 JPEG before the image ever leaves the device.
 *
 * Doing this client-side rather than server-side is the whole point: a modern
 * phone camera produces four to eight megabytes per shot, and ten of those queued
 * in an outbox is a sync that will not complete over a site's mobile signal. The
 * resize happens once, at capture, so what waits in the queue is already small.
 *
 * Aspect ratio is preserved and the image is never scaled UP — a photo already
 * smaller than the bound passes through re-encoded but not stretched.
 */
export async function resizeImage(file: Blob): Promise<ResizedImage> {
  const bitmap = await loadBitmap(file);

  const scale = Math.min(MAX_WIDTH / bitmap.width, MAX_HEIGHT / bitmap.height, 1);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the photo for upload.');
  context.drawImage(bitmap as CanvasImageSource, 0, 0, width, height);

  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);

  return {
    data: base64,
    contentType: 'image/jpeg',
    width,
    height,
    // Approximate: base64 carries three bytes in every four characters.
    byteLength: Math.floor((base64.length * 3) / 4),
  };
}

async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }

  // Older Safari, and the jsdom environment the component tests run in.
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The photo could not be read.'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
